import React, { useCallback, useEffect, useRef, useState } from 'react';

const logoItemClass =
  'h-8 sm:h-10 w-auto max-w-[140px] sm:max-w-[160px] object-contain opacity-90 grayscale-[20%] hover:grayscale-0 transition-[filter] duration-300';

/**
 * Seamless infinite horizontal scroll — duplicate logo strip, animate by measured strip width.
 * @param {{ logos: Array<{ src: string, alt: string }> }} props
 */
const TrustLogoMarquee = ({ logos }) => {
  const stripRef = useRef(null);
  const trackRef = useRef(null);
  const [marqueeReady, setMarqueeReady] = useState(false);

  const measureStrip = useCallback(() => {
    const strip = stripRef.current;
    const track = trackRef.current;
    if (!strip || !track) return;

    const width = strip.offsetWidth;
    if (width <= 0) {
      setMarqueeReady(false);
      return;
    }

    track.style.setProperty('--marquee-distance', `-${width}px`);
    setMarqueeReady(true);
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return undefined;

    measureStrip();

    const observer = new ResizeObserver(() => {
      measureStrip();
    });
    observer.observe(strip);

    const images = strip.querySelectorAll('img');
    const onImageLoad = () => measureStrip();
    images.forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', onImageLoad);
        img.addEventListener('error', onImageLoad);
      }
    });

    return () => {
      observer.disconnect();
      images.forEach((img) => {
        img.removeEventListener('load', onImageLoad);
        img.removeEventListener('error', onImageLoad);
      });
    };
  }, [logos, measureStrip]);

  const renderStrip = (stripKey, ariaHidden = false, ref = null) => (
    <ul
      key={stripKey}
      ref={ref}
      className="flex items-center shrink-0 list-none m-0 p-0"
      aria-hidden={ariaHidden || undefined}
    >
      {logos.map((logo) => (
        <li key={`${stripKey}-${logo.alt}`} className="flex shrink-0 items-center px-8 sm:px-12">
          <img
            src={logo.src}
            alt={ariaHidden ? '' : logo.alt}
            className={logoItemClass}
            loading="eager"
            decoding="async"
            height={40}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <div className="promo-logo-marquee w-full overflow-hidden">
      <div className="promo-logo-marquee-fade">
        <div
          ref={trackRef}
          className={`promo-logo-marquee-track flex w-max${
            marqueeReady ? ' promo-logo-marquee-track--active' : ''
          }`}
        >
          {renderStrip('a', false, stripRef)}
          {renderStrip('b', true)}
        </div>
      </div>
    </div>
  );
};

export default TrustLogoMarquee;
